"use client";

import {
  useFormatter,
  useLocale,
  useTimeZone,
  useTranslations,
} from "next-intl";
import { useCallback, useMemo } from "react";
import { type DateFormatters, createDateFormatters } from "./date-formatters";
import { type Locale, localeBcp47 } from "./locales";

/**
 * Locale-aware formatting for dates, numbers and currency.
 *
 * Replaces the ~525 places that hardcode `"de-DE"`. Two categories must NOT be
 * routed through here, and are documented as exemptions instead:
 *
 *   - **Machine formats.** `settings-usage-format.ts` uses `"en-CA"` to get
 *     `YYYY-MM-DD` and `settings-usage-fine-time.ts` uses `"en-GB"`. Those are
 *     data formats; translating them would corrupt the values.
 *   - **Sort keys.** `compare-german-labels.ts` and the persisted
 *     `properties.name_sort` column must stay German-collated — a list whose
 *     order changes with the interface language breaks cursor pagination.
 *
 * The time zone is pinned to Europe/Berlin in `src/i18n/request.ts`, so server
 * and client render identical strings regardless of where the server runs.
 */

/** BCP 47 tag for the active locale — for the rare direct `Intl` use. */
export function useLocaleTag(): string {
  const tLocales = useTranslations("common.locales");
  const locale = useLocale() as Locale;
  return localeBcp47(tLocales)[locale];
}

export type { DateFormatters } from "./date-formatters";

function useRelativeFormatter(): DateFormatters["relative"] {
  const format = useFormatter();
  return useCallback(
    // Explicit clock: no provider `now` (would freeze labels), else next-intl logs ENVIRONMENT_FALLBACK.
    (value, now) =>
      format.relativeTime(toDate(value), toDate(now ?? Date.now())),
    [format],
  );
}

export function useFormatDate(): DateFormatters {
  const localeTag = useLocaleTag();
  const timeZone = useTimeZone();
  const relative = useRelativeFormatter();
  return useMemo(
    () => createDateFormatters({ localeTag, timeZone, relative }),
    [localeTag, timeZone, relative],
  );
}

/**
 * Device-zone formatters for dates built from the device clock (calendar days).
 * Berlin pin would print Sunday over Monday east of Berlin.
 */
export function useFormatLocalDate(): DateFormatters {
  const localeTag = useLocaleTag();
  const relative = useRelativeFormatter();
  return useMemo(
    () => createDateFormatters({ localeTag, timeZone: undefined, relative }),
    [localeTag, relative],
  );
}

export interface NumberFormatters {
  /** 1.234,5 · 1,234.5 */
  readonly decimal: (value: number, fractionDigits?: number) => string;
  /** 1.234,50 € · €1,234.50 */
  readonly currency: (value: number, currency?: string) => string;
  /** 42 % · 42% */
  readonly percent: (value: number) => string;
}

export function useFormatNumber(): NumberFormatters {
  const format = useFormatter();

  return useMemo(
    () => ({
      decimal: (value, fractionDigits) =>
        format.number(value, {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }),
      // Euro stays the default: the product is German property management, so
      // the currency follows the money, not the reader's language.
      currency: (value, currency = "EUR") =>
        format.number(value, { style: "currency", currency }),
      percent: (value) => format.number(value, { style: "percent" }),
    }),
    [format],
  );
}

function toDate(value: Date | number): Date {
  return value instanceof Date ? value : new Date(value);
}

export interface DurationFormatters {
  /** 7:30 h (minutes in) */
  readonly hours: (minutes: number) => string;
  /** +1:15 h · −0:30 h · 0:00 h */
  readonly signedHours: (minutes: number) => string;
  /** 7:30 (no unit, for dense grids) */
  readonly clock: (minutes: number) => string;
  /** 1,5 · 1.5 (days, half-day precision) */
  readonly days: (days: number) => string;
}

/** Durations stay H:MM in every locale; only the day count is locale-formatted. */
export function useFormatDuration(): DurationFormatters {
  const format = useFormatter();
  return useMemo(() => {
    const clock = (minutes: number): string => {
      const abs = Math.abs(Math.round(minutes));
      const h = Math.floor(abs / 60);
      const m = abs % 60;
      return `${h}:${m.toString().padStart(2, "0")}`;
    };
    return {
      hours: (minutes) => `${minutes < 0 ? "−" : ""}${clock(minutes)} h`,
      signedHours: (minutes) => {
        const rounded = Math.round(minutes);
        const sign = rounded > 0 ? "+" : rounded < 0 ? "−" : "";
        return `${sign}${clock(rounded)} h`;
      },
      clock: (minutes) => `${minutes < 0 ? "−" : ""}${clock(minutes)}`,
      days: (days) => format.number(days, { maximumFractionDigits: 1 }),
    };
  }, [format]);
}

/** Compact age for list rows: de „9 Min.“ / „3 Tg.“, en „9 min“ / „3 days“. Non-hook: callers pass the locale tag. */
export function formatCompactAge(
  value: number,
  unit: "minute" | "hour" | "day",
  localeTag: string,
): string {
  return new Intl.NumberFormat(localeTag, {
    style: "unit",
    unit,
    unitDisplay: "short",
  }).format(value);
}
